import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiUser, FiMail, FiPhone, FiMessageCircle, FiCheckCircle } from "react-icons/fi";
import "./index.css";

type FormData = {
  name: string;
  email: string;
  phone: string;
  notes: string;
  industry: string;
};

type Errors = Partial<Record<keyof FormData, string>> & {
  submit?: string;
};

type QuoteModalProps = {
  open: boolean;
  onClose: () => void;
};

const API_BASE = "https://bioclockin-email-server.vercel.app/send-email";

export default function QuoteModal({ open, onClose }: QuoteModalProps) {
  const firstFieldRef = useRef<HTMLInputElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [done, setDone] = useState<boolean>(false);

  const [form, setForm] = useState<FormData>({
    name: "",
    email: "",
    phone: "",
    notes: "",
    industry: "",
  });

  const [errors, setErrors] = useState<Errors>({});

  // Lock body scroll + focus first field + Esc to close
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const t = setTimeout(() => firstFieldRef.current?.focus(), 0);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = prevOverflow;
      clearTimeout(t);
      window.removeEventListener("keydown", onKey);
      setLoading(false);
    };
  }, [open, onClose]);

  if (!open) return null;

  const validate = (): boolean => {
    const e: Errors = {};
    if (!form.name.trim()) e.name = "Your name is required";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Enter a valid email";
    if (form.phone && !/^[\d+\-\s()]{6,}$/.test(form.phone)) e.phone = "Enter a valid phone";
    if (!form.industry) e.industry = "Please choose an industry";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const res = await fetch(API_BASE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          message: form.notes,
          industry: form.industry,
          source: "bioclock-in.com",
        }),
      });

      if (!res.ok) throw new Error("Failed to send");
      setDone(true);
      setErrors({});
    } catch (err) {
      console.error(err);
      setErrors((prev) => ({ ...prev, submit: "Could not send. Please try again." }));
    } finally {
      setLoading(false);
    }
  };

  const closeAndReset = () => {
    if (loading) return; // prevent closing mid-send
    setForm({ name: "", email: "", phone: "", notes: "", industry: "" });
    setErrors({});
    setDone(false);
    onClose();
  };

  return createPortal(
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="quoteTitle">
      <div className="modal__backdrop" onClick={closeAndReset} />
      <div className="modal__panel modal__panel--elevate" ref={panelRef}>
        <div className="modal__header modal__header--gradient">
          <h2 id="quoteTitle" className="modal__title">Request Demo</h2>
          <button className="modal__close" aria-label="Close" onClick={closeAndReset}>×</button>
        </div>

        {done ? (
          <div className="modal__success">
            <div className="success__icon">
              <FiCheckCircle aria-hidden={true} />
            </div>
            <h3 className="success__title">Thanks! We’ve got your request.</h3>
            <p className="success__text">
              We’ll reach out shortly with pricing and device options that fit your team.
            </p>
            <div className="form__actions">
              <button type="button" className="btn btn--primary" onClick={closeAndReset}>
                Close
              </button>
            </div>
          </div>
        ) : (
          <form className="form form--grid" onSubmit={handleSubmit} noValidate>
            <div className={`form__row ${errors.name ? "has-error" : ""}`}>
              <label htmlFor="q-name">Full name</label>
              <div className="input__wrap">
                <div className="input__icon"><FiUser aria-hidden={true} /></div>
                <input
                  id="q-name"
                  ref={firstFieldRef}
                  type="text"
                  placeholder="Your name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  aria-invalid={!!errors.name}
                  aria-describedby={errors.name ? "err-name" : undefined}
                  required
                />
              </div>
              {errors.name && <div id="err-name" className="error">{errors.name}</div>}
            </div>

            <div className={`form__row ${errors.email ? "has-error" : ""}`}>
              <label htmlFor="q-email">Email</label>
              <div className="input__wrap">
                <div className="input__icon"><FiMail aria-hidden={true} /></div>
                <input
                  id="q-email"
                  type="email"
                  placeholder="you@company.co.za"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? "err-email" : undefined}
                  required
                />
              </div>
              {errors.email && <div id="err-email" className="error">{errors.email}</div>}
            </div>

            <div className={`form__row ${errors.phone ? "has-error" : ""}`}>
              <label htmlFor="q-phone">Phone</label>
              <div className="input__wrap">
                <div className="input__icon"><FiPhone aria-hidden={true} /></div>
                <input
                  id="q-phone"
                  type="tel"
                  placeholder="+27 ..."
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  aria-invalid={!!errors.phone}
                  aria-describedby={errors.phone ? "err-phone" : undefined}
                />
              </div>
              {errors.phone && <div id="err-phone" className="error">{errors.phone}</div>}
            </div>

            <div className={`form__row ${errors.industry ? "has-error" : ""}`}>
              <label htmlFor="q-industry">Industry</label>
              <select
                id="q-industry"
                value={form.industry}
                onChange={(e) => setForm({ ...form, industry: e.target.value })}
                aria-invalid={!!errors.industry}
                aria-describedby={errors.industry ? "err-industry" : undefined}
                required
              >
                <option value="">Select an industry</option>
                <option value="Construction">Construction</option>
                <option value="Mining & Heavy Industry">Mining & Heavy Industry</option>
                <option value="Agriculture">Agriculture</option>
                <option value="Security & Cleaning">Security & Cleaning</option>
                <option value="Other">Other</option>
              </select>
              {errors.industry && <div id="err-industry" className="error">{errors.industry}</div>}
            </div>

            <div className="form__row form__row--full">
              <label htmlFor="q-notes">What do you need?</label>
              <div className="input__wrap input__wrap--textarea">
                <div className="input__icon"><FiMessageCircle aria-hidden={true} /></div>
                <textarea
                  id="q-notes"
                  rows={4}
                  placeholder="Offline tablet setup, number of sites, users, timeline..."
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                />
              </div>
            </div>

            <div className="form__fine">
              <label className="check">
                <input type="checkbox" required /> I agree to be contacted about this request.
              </label>
              <small className="muted">
                We don’t share your details. See our <a href="/privacy">privacy policy</a>.
              </small>
            </div>

            <div className="form__actions">
              <button type="submit" className="btn btn--primary btn--wide" disabled={loading}>
                {loading ? "Sending…" : "Send request"}
              </button>
            </div>

            {errors.submit && (
              <div role="alert" className="error" style={{ marginTop: "0.5rem" }}>
                {errors.submit}
              </div>
            )}
          </form>
        )}
      </div>
    </div>,
    document.body
  );
}
